import React from 'react';
import styled from 'styled-components';
import { Body7, Container } from '@ui';
import { NumberField, Select } from '@ui/inputs';
import { DocTypeSelect } from './DocTypeSelect';
import { FlatButton, TransparentFlatButton } from '@ui/buttons';
import { SpecEditorContext } from './SpecEditorContext';
import { Consequence, MicroLabel } from './styles';

// The `[{ doc_type, latest_n? }]` rule list used by `filter` and `related_filter`.
//
// `latest_n` is a THREE-state control, not a number with a default, and that is the whole reason
// this component exists. Omitted means "every matching document"; an integer caps to the N most
// recent. Under `related_filter` the cap is per RELATED COMPANY, not a total — so a rule reading
// "latest 1" against a company with forty peers pins forty documents, not one. Both of those
// consequences are printed next to the control that causes them rather than left in a doc
// elsewhere.
//
// Document types are picked from the app-wide `docTypes` constant, with a free-text escape for the
// many real types that constant does not carry — see DocTypeSelect. Nothing is validated against
// it: a type the engine does not index is not an error, the spec just never selects anything.

const Rule = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(180px, 2fr) minmax(120px, 1fr) auto;
  align-items: start;
  padding: 12px 0;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  }

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

const ALL = '__all__';

export const DocTypeRulesEditor = ({ basePath, perRelatedCompany }) => {
  const { definition, setValue, removeValue } = React.useContext(SpecEditorContext);

  const documents = ((definition || {}).document_set || {}).documents || {};
  const rules = Array.isArray(documents.doc_types) ? documents.doc_types : [];

  const seen = {};
  const duplicates = {};
  rules.forEach((rule) => {
    const type = rule && rule.doc_type;
    if (!type) return;
    if (seen[type]) duplicates[type] = true;
    seen[type] = true;
  });

  // `rules` falls back to [] whenever doc_types is not an array, so writing to `${basePath}.N`
  // would add a numeric KEY to whatever object is really there — invisible in the form, and
  // overwritten by the next click. Replace the container instead.
  const isList = Array.isArray(documents.doc_types);
  const addRule = () =>
    isList ? setValue(`${basePath}.${rules.length}`, { doc_type: '' }) : setValue(basePath, [{ doc_type: '' }]);
  const removeRule = (index) => removeValue(`${basePath}.${index}`);

  return (
    <div>
      <MicroLabel>Document types</MicroLabel>

      {rules.length === 0 && (
        <Container top={8}>
          <Consequence $tone="bad">No document types listed. This spec selects nothing and cannot lock.</Consequence>
        </Container>
      )}

      {!isList && documents.doc_types !== undefined && (
        <Container top={8}>
          <Consequence $tone="bad">
            {`\`doc_types\` is not a list here — it is ${typeof documents.doc_types}. Adding a rule replaces it.`}
          </Consequence>
        </Container>
      )}

      {rules.map((rule, index) => {
        // A bare string arrives when doc_types was authored for `screen`, whose element type is a
        // plain name. Show it rather than rendering an empty box the first keystroke would erase.
        const bareString = typeof rule === 'string';
        const docType = bareString ? rule : (rule && rule.doc_type) || '';
        const hasCap = !bareString && rule && rule.latest_n !== undefined && rule.latest_n !== null;
        return (
          // eslint-disable-next-line react/no-array-index-key
          <Rule key={`doc-type-rule-${index}`}>
            <DocTypeSelect
              label={index === 0 ? 'Document type' : undefined}
              ariaLabel={`Document type, rule ${index + 1}`}
              value={docType}
              onChange={(v) => setValue(`${basePath}.${index}`, { ...(bareString ? {} : rule), doc_type: v })}
              invalid={!docType || !!duplicates[docType]}
              hint={
                bareString
                  ? 'Carried over as a bare name from a screen. Choosing a type converts this row to a rule.'
                  : duplicates[docType]
                  ? 'Listed twice — each document type may appear once.'
                  : undefined
              }
            />

            <div>
              <Select
                label={index === 0 ? 'How many' : undefined}
                aria-label={`How many, rule ${index + 1}`}
                value={hasCap ? 'capped' : ALL}
                onChange={(v) => {
                  if (v === ALL) removeValue(`${basePath}.${index}.latest_n`);
                  else setValue(`${basePath}.${index}.latest_n`, 1);
                }}
                options={[
                  { value: ALL, label: 'Every matching document' },
                  { value: 'capped', label: 'Only the most recent N' },
                ]}
              />
              {hasCap && (
                <Container top={8}>
                  <NumberField
                    min={1}
                    aria-label={`Most recent N, rule ${index + 1}`}
                    value={rule.latest_n}
                    onChange={(v) => setValue(`${basePath}.${index}.latest_n`, v)}
                    invalid={!Number.isInteger(Number(rule.latest_n)) || Number(rule.latest_n) <= 0}
                    hint={perRelatedCompany ? 'Per related company' : 'Newest first, as of the occasion date'}
                  />
                </Container>
              )}
            </div>

            <Container top={index === 0 ? 22 : 0}>
              <TransparentFlatButton onClick={() => removeRule(index)}>Remove</TransparentFlatButton>
            </Container>

            <div style={{ gridColumn: '1 / -1' }}>
              <Consequence>
                {hasCap
                  ? perRelatedCompany
                    ? `Pins the ${rule.latest_n} most recent ${
                        docType || 'documents'
                      } for EACH related company inside the window — not ${rule.latest_n} in total.`
                    : `Pins the ${rule.latest_n} most recent ${
                        docType || 'documents'
                      } the company itself published on or before the occasion date.`
                  : perRelatedCompany
                  ? `Pins EVERY ${
                      docType || 'matching document'
                    } in the window, for every related company. With a wide window this can be a very large set.`
                  : `Pins EVERY ${
                      docType || 'matching document'
                    } the company published on or before the occasion date.`}
              </Consequence>
            </div>
          </Rule>
        );
      })}

      <Container top={16}>
        <FlatButton onClick={addRule}>Add a document type</FlatButton>
      </Container>

      <Container top={8}>
        <Body7 isLowContrast>
          A type the engine does not index is not an error — the spec loads cleanly and simply never selects anything.
        </Body7>
      </Container>
    </div>
  );
};
